import React, { useCallback, useMemo, useRef } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import util from '../../util';
import connectionService from '../../services/Connection';
import LoadingDialog from '../../components/common/LoadingDialog';
import strings from '../../Globalization';
import consts from '../../consts';
import { useLiveRef, useSetState, useUniqueEffect } from '../../hooks';
import { usePrerequisites } from '../Prerequisites';
import CIResultFormDialog from './CIResultFormDialog';
import CycleInfoContextProvider from './Context';
import CycleBreadcrumbs from './CycleBreadcrumbs';
import CycleReviewFormDialog from './CycleReviewFormDialog';
import XmlEditorDialog from './XmlEditorDialog';

const DTE_FIELDS = [
	'ActualConditionTime',
	'ActualSterilizeTime',
	'ChamberOpenedTime',
	'ContactTimeRecorded',
	'CreatedOn',
	'CycleDateTime',
	'CycleStart',
	'CycleDateTime',
	'CycleEnd',
	'DryTime',
	'MaxFillTime',
	'MaxFillTimeActual',
	'MinFillTime',
	'ModifiedOn',
	'ReviewedDate',
	'ReviewTimeStamp',
	'SterilizeTimeActual',
	'SterilizeTimeMetric',
	'TargetExposureTime',
	'TotalTime',
	'VHPExpirationActual',
];


export const CycleInfoProvider = ({
	value,
	type,
	children,
	comboTypes = ['CycleActionStatus'],
	hideBreadCrumbs,
	id: idProp,
	onDataLoad,
	onGetPrintDetailsParams,
	dataUrl = connectionService.EndoSystemCycleList,
	breadcrumbsProps,
	onFetchCIResultFormData
}) => {
	const [, { navigateToLastAccessibleRoute }] = usePrerequisites();
	const theme = useTheme();
	const tapeRef = useRef();
	const stockChartRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const fullscreenDialogs = useMediaQuery(theme.breakpoints.down('md'));
	const clientId = 0;
	const [state, setState] = useSetState({
		data: null,
		combos: {},
		loading: false,
		loadingMessage: null,
		isLog: true,
		error: null,
		tags: {},
		modalIsXmlOpen: false,
		xmlEditorValue: '',
		view: 'details',
		isChartPopUp: false,
		filterOptions: {},
		isShowInstrument: false,
		filters: {},
		disablePrev: false,
		disableNext: false,
		isXmlProcess: false,
		ciResultFormDialogOpen: false,
		ciResultFormData: null,
		cycleReviewFormDialogOpen: false
	});

	const dataRef = useLiveRef(state.data)
	const id = useMemo(
		() => idProp || (searchParams.has('id') ? searchParams.get('id') : dataRef.current?._id),
		[searchParams,  idProp]
	);
	const attachmentCount = useMemo(
		() =>
			state.data
				? dataRef.current?.AttachmentCount || 0
				: searchParams.has('attachmentCount')
				? parseInt(searchParams.get('attachmentCount'))
				: 0,
		[searchParams, state.data]
	);
	const cycleId = useMemo(
		() =>
			dataRef.current
				? dataRef.current?.CycleId || 0
				: searchParams.has('cycleId')
				? searchParams.get('cycleId')
				: 0,
		[searchParams, state.data]
	);

	const equipmentFamilyId = useMemo(
		() =>
			dataRef.current?.data
				? dataRef.current?.EquipmentFamilyId
				: searchParams.has('equipmentFamilyId')
				? searchParams.get('equipmentFamilyId')
				: 0,
		[searchParams, state.data]
	);

	const fetchData = useCallback(async () => {
		setState({
			loading: true
		});
		let comboTypesArr = [];
		if (Array.isArray(comboTypes)) {
			for (const comboType of comboTypes) {
				comboTypesArr.push({ type: comboType, loaded: true });
			}
		}
		let formData = new FormData();

		formData.append('action', 'load');
		formData.append('attachmentCount', attachmentCount);
		formData.append('id', id);
		formData.append('EquipmentFamilyId', equipmentFamilyId);
		formData.append('authToken', null);
		formData.append('comboTypes', JSON.stringify(comboTypesArr));

		return await fetch(dataUrl, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.json())
			.then((json) => {
				const { success, data, combos, info } = json;
				if (success) {
					DTE_FIELDS.forEach((field) => {
						if (field in data && !!data[field]) {
							const dteFieldValue = util.fixDates(data[field]);
							json.data[field] = dteFieldValue;
							data[field] = dteFieldValue;
						}
					});
					data.id = data.id?? data._id 
					setState({
						data,
						combos
					});
					if (typeof onDataLoad === 'function') {
						onDataLoad(json);
					}
				}
				else {
					Swal.fire({ titleText: strings.ErrorTitle, text: info? strings[info] || info : strings.Error, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				}
				
				return json;
			})
			.catch((err) => {})
			.finally(() =>
				setState({
					loading: false
				})
			);
	}, [attachmentCount, comboTypes, id, onDataLoad, dataUrl]);

	const onOpenXmlModal = useCallback(() => {
		setState({ loading: true });
		const xmlEditorTitle = `${strings.XMLName} ${dataRef.current?.EquipmentTypeName}/${dataRef.current?.SerialNumber}`;
		const formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'PreviewXML');
		formData.append('cycleId', dataRef.current?.CycleId);
		fetch(connectionService.CycleList, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.text())
			.then((xmlEditorValue) => {
				setState({ xmlEditorOpen: true, xmlEditorValue, loading: false, xmlEditorTitle });
			})
			.catch((error) => setState({ error }))
			.finally(() => setState({ loading: false }));
	}, [state.data]);

	const onCloseXmlModal = () => {
		setState({ xmlEditorOpen: false });
	};

	const onXmlChange = (value) => {
		setState({ xmlEditorValue: value });
	};

	const onReprocessXml = useCallback(() => {
		setState({ isXmlProcess: true });
		let formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'ReprocessXml');
		formData.append('id', id);
		util.fetchRequest({ url: connectionService.CycleList, formData: formData }, (response) => {
			if (response.status === 200) {
				Swal.fire({ titleText: strings.Success, text: strings.XmlReprocessed, icon: 'success', allowOutsideClick: false });
			} else {
				Swal.fire({ titleText: strings.Success, text: strings.XmlProcessingFailed, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
			}
			setState({ isXmlProcess: false });
		});
	}, [id]);

	const onOpenChartPopUp = (popupChartOpen) => setState({ popupChartOpen: popupChartOpen });

	const onPhasesFilter = (filters) => {
		setState({
			filters
		});
	};

	const getStockChartImageBase64Data = async () => {
		const element = stockChartRef.current;
		if (element) {
			return await html2canvas(element).then((canvas) => canvas.toDataURL('image/jpg')).catch((error)=> {
				console.error('Error getting stock chart data:', error)
				return;
			});
		}
		return;
	};
	const onOpenCIResultFormDialog = useCallback(() => {
		if (typeof onFetchCIResultFormData === 'function') {
			setState({ loading: true });
			onFetchCIResultFormData({ data: state.data })
				.then(({ combos: ciResultCombos }) => {					
					setState((prevState) => ({
						combos: { ...prevState.combos, ...ciResultCombos },
						loading: false,
						ciResultFormDialogOpen: true
					}));
				})
				.finally(() => setState({ loading: false }));
		}
		else {
			setState({ ciResultFormData: null, ciResultFormDialogOpen: true });
		}
		
	}, [onFetchCIResultFormData, setState, state]);
	const onCloseCIResultFormDialog = (reload) => {
		setState({ ciResultFormDialogOpen: false, ciResultFormData: null });
		if (reload) {
			fetchData();
		}
	};

	const onOpenCycleReviewFormDialog = (approvalStatusId) =>
		setState({ cycleReviewFormDialogOpen: true, approvalStatusId });

	const onCloseCycleReviewFormDialog = (reload = false) => {
		setState({ cycleReviewFormDialogOpen: false });
		if (reload) {
			fetchData();
		}
	};

	const onCloseXmlEditorDialog = () =>
		setState({ xmlEditorOpen: false, xmlEditorValue: undefined, xmlEditorTitle: undefined });

	const onPrintTape = useCallback(() => {
		if (tapeRef.current?.download) {
			const { SerialNumber, CycleName, CycleCount, CycleStart } = state.data;
			const filename = `${SerialNumber}_${CycleName.replace(/ /g, '')}_${CycleCount}_${dayjs(CycleStart).format(
				consts.MomentFormat.MonthDateYear
			)}_Print_Tape`;
			setState({ loading: true });
			tapeRef.current.download(filename, 'pdf').finally(() => setState({ loading: false }));
		}
	}, [state.data]);

	const onPreviewTape = () => {
		if (tapeRef.current?.print) {
			tapeRef.current.print();
		}
	};

	const onPrintDetails = useCallback(
		async (preview = false) => {
			const data = dataRef.current;
			if (!data) {
				return;
			}
			setState({ loading: true });			
			const stockChart = await getStockChartImageBase64Data();
			const params = await onGetPrintDetailsParams({ data });

			const formData = new FormData();
			for (let { Name, Value } of params) {
				formData.set(Name, Value);
			}

			if (stockChart) {
				formData.set('stockChart', stockChart);
			}

			formData.set('noPhasesDataAvailable', strings.NoChartDataAvailableForCycle);
			return fetch(connectionService.CycleList, { method: 'POST', body: formData })
				.then((res) => res.blob())
				.then((blob) => {
					if (blob instanceof Blob) {
						const filename = `${data?.SerialNumber}_${data.CycleName.replace(/ /g, '')}_${
							data.CycleCount
						}_${dayjs(data.CycleStart).format(consts.MomentFormat.MonthDateYear)}`;
						const href = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = href;
						document.body.appendChild(link);
						if (preview) {
							link.setAttribute('target', `_blank`);
							link.setAttribute('type', `application/pdf`);
						} else {
							link.setAttribute('download', `${filename}.pdf`);
						}
						//
						link.click();
						document.body.removeChild(link);
					}
				})
				.catch((err) => {
					console.error(err);
					Swal.fire({
						titleText: strings.Error,
						text: strings[`Status_${err?.status}`] || err?.msg,
						icon: 'error',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				})
				.finally(() => setState({ loading: false }));
		},
		[onGetPrintDetailsParams, id]
	);

	const onPreviewDetails = useCallback(() => onPrintDetails(true), [onPrintDetails]);
	const handleOnPrintDetails = useCallback(() => onPrintDetails(false), [onPrintDetails]);

	const handleOnLoadNextOrPrev = useCallback(
		({ id, equipmentId, type, isIncrement }) => {
			const formData = new FormData();
			setState({
				loading: true
			});
			
			const SerialNumber = dataRef.current?.SerialNumber || '';
			equipmentId = equipmentId?._id || equipmentId;
			formData.append('action', 'otherAction');
			formData.append('otherAction', 'NextPrevHandler');
			formData.append('equipmentId', equipmentId);
			formData.append('id', id);
			formData.append('asArray', false);
			formData.append('type', type);
			formData.append('isIncrement', isIncrement);
			fetch(connectionService.CycleList, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					if (json.data?.length) {
						const data = json.data[0];
						const newId = data?._id;
						if (newId) {
							const searchParamsObj = {};
							for (const [key, value] of searchParams.entries()) {
								searchParamsObj[key] = value;
							}

							if ('id' in searchParamsObj) {
								searchParamsObj.id = newId;
							}
							if ('attachmentCount' in searchParamsObj) {
								searchParamsObj.attachmentCount = data.AttachmentCount || 0;
							}
							if ('sterilizerId' in searchParamsObj) {
								searchParamsObj.sterilizerId = data.SterilizerCycleId || 0;
							}
							if ('equipmentFamilyId' in searchParamsObj) {
								searchParamsObj.equipmentFamilyId =
									data.EquipmentFamilyId || data.EquipmentTypeId || searchParamsObj.equipmentFamilyId;
							}

							setSearchParams(searchParamsObj);
						}
					} else {
						setState({
							loading: false
						});
						let message = isIncrement
							? strings.NextPage.replace('{0}', SerialNumber)
							: strings.PrevPage.replace('{0}', SerialNumber);
						Swal.fire({
							titleText: strings.Info,
							text: message,
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					}
				})
				.catch((err) => {
					console.error(err);
					setState({
						loading: false
					});
					Swal.fire({
						titleText: strings.Error,
						text: strings[`Status_${err?.status}`],
						icon: 'error',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				});
		},
		[id, searchParams, state.data]
	);

	const onNext = useCallback(() => {
		handleOnLoadNextOrPrev({
			id: dataRef.current?._id,
			equipmentId: dataRef.current?.EquipmentId,
			type,
			isIncrement: true
		});
	}, [type]);

	const onPrevious = useCallback(() => {
		handleOnLoadNextOrPrev({
			id: dataRef.current?._id,
			equipmentId: dataRef.current?.EquipmentId,
			type,
			isIncrement: false
		});
	}, [type]);

	useUniqueEffect(() => {
		if (id) {
			fetchData();
		} else {
			navigateToLastAccessibleRoute(`Invalid Cycle ID: ${id}`);
		}
	}, [id]);

	return (
		<CycleInfoContextProvider
			value={{
				...state,
				type,
				clientId,
				tapeRef,
				stockChartRef,
				onOpenCycleReviewFormDialog,
				onOpenCIResultFormDialog,
				onCloseCIResultFormDialog,
				onOpenXmlModal,
				onReprocessXml,
				onCloseXmlEditorDialog,
				onPrintTape,
				onPreviewTape,
				onPreviewDetails,
				cycleTapeDisplayed: state.data?.CycleTape?.length > 0,
				onCloseCycleReviewFormDialog,
				cycleId,
				equipmentFamilyId,
				fullscreenDialogs,
				fetchData,
				onNext,
				onPrevious,
				onXmlChange,
				onCloseXmlModal,
				onOpenChartPopUp,
				onPhasesFilter,
				onPrintDetails: handleOnPrintDetails,
				getStockChartImageBase64Data,
				...value
			}}
		>
			<Grid container>
				<Grid size={12}>
					<LoadingDialog isOpen={state.loading} message={state.loadingMessage} />
				</Grid>

				<Grid size={12} container >
					{!hideBreadCrumbs && <CycleBreadcrumbs {...breadcrumbsProps} />}
					<CIResultFormDialog />
					<CycleReviewFormDialog />
					<XmlEditorDialog 
						xmlEditorOpen={state.xmlEditorOpen}
						xmlEditorValue={state.xmlEditorValue}
						xmlEditorTitle={state.xmlEditorTitle}
						onCloseXmlEditorDialog={onCloseXmlEditorDialog}
						xmlFileName={state.data?.StorageFileName}
					 />
				</Grid>
			</Grid>
			<Grid container>{children}</Grid>
		</CycleInfoContextProvider>
	);
};
export default CycleInfoProvider;
